import { sparkline } from '@anytracker/core'

import styles from './Sparkline.module.css'

interface Props {
  /** Recent readings, oldest first. Already quantified by core. */
  readonly values: readonly number[]
  readonly colour: string
}

const WIDTH = 60
const HEIGHT = 24
const INSET = 2

/**
 * A track's last few readings, at thumbnail size (T-907).
 *
 * The arithmetic is core's; this only turns a unit box into pixels. Renders
 * **nothing at all** when there is nothing to draw — a track nobody has logged
 * gets no line rather than a flat one along the bottom, which would read as
 * "recorded, always zero".
 *
 * Hidden from assistive technology. At this size the shape is suggestive rather
 * than readable, and the row states the count in words beside it — which is the
 * form of this information anybody can actually use.
 */
export function Sparkline({ values, colour }: Props) {
  const points = sparkline(values)
  if (points === null) {
    return null
  }

  const path = points
    .map((point, index) => {
      const x = INSET + point.x * (WIDTH - INSET * 2)
      // SVG y grows downward; the unit box grows up.
      const y = HEIGHT - INSET - point.y * (HEIGHT - INSET * 2)
      return `${index === 0 ? 'M' : 'L'}${x.toFixed(2)} ${y.toFixed(2)}`
    })
    .join(' ')

  return (
    <svg
      className={styles['spark']}
      viewBox={`0 0 ${String(WIDTH)} ${String(HEIGHT)}`}
      width={WIDTH}
      height={HEIGHT}
      fill="none"
      aria-hidden="true"
    >
      <path
        d={path}
        stroke={colour}
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
